import { cloneDeep } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { PageTitle, Button, Alert } from '@gsa/afp-component-library';
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTitle } from '@gsa/afp-shared-ui-utils';
import { VEHICLE_REQUEST_TAB } from '../utils/additional-utils';
import SelectedVehicleDetails from './selected-vehicle-details';
import AdditionalsVehicleGarage from './steps/vehicle-garage/additionals-vehicle-garage';
import AdditionalsVehicleTabs from '../components/vehicle-selection/additional-vehicle-tabs';
import AdditionalsReviewSubmit from './steps/review-submit/additionals-review-submit';
import AdditionalActivities from '../components/activity/activity-comment';
import { TabSet } from '@gsa/afp-component-library';
import {
  useCreateAdditionalRequestNote,
  useGetAdditionalRequestLazy,
  useGenerateReadSignedUrl,
  useDocMetaData,
} from '../../../requests/additionals-request';
import fileDownload from 'js-file-download';
import { setAdditionalRequestActivities } from '../../../reducers/additionals-reducer';
import CommentModal from '../../../components/CommentModal/CommentModal';
import AdditionalsSelectedVehicle from '../components/selected-vehicle/selected-vehicle';
import { ADDITIONAL_REQUEST_STATUS } from '../utils/additional-utils';

export const ADDITIONAL_VEHICLE_STEPS = Object.freeze({
  GARAGE: 'garage',
  SELECT_OPTIONS: 'select-options',
  REVIEW_SUBMIT: 'review-submit',
});

export default function VehicleRequestSteps() {
  useTitle('Additionals | Leasing');
  const { requestId } = useParams();
  const dispatch = useDispatch();
  const {
    currentStep,
    agencyGuidanceDocument,
    selectedCustomerAccount,
    delinquencyCheck,
    additionalsDraftData,
  } = useSelector((state) => state.additionalsReducer);
  const [tab, _setTab] = useState(VEHICLE_REQUEST_TAB.SELECTION_DETAILS);
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [createAdditionalRequestNote] = useCreateAdditionalRequestNote();
  const [getAdditionalRequest, { loading: isLoading }] =
    useGetAdditionalRequestLazy();

  const [getSignedUrl] = useGenerateReadSignedUrl();
  const [getDocMetadata] = useDocMetaData();

  const noDeliquencyCheckStatus =
    additionalsDraftData?.requestStatusCode ===
      ADDITIONAL_REQUEST_STATUS.APPROVED_AT_FSR ||
    additionalsDraftData?.requestStatusCode ===
      ADDITIONAL_REQUEST_STATUS.REQUISITION_CREATED ||
    additionalsDraftData?.requestStatusCode ===
      ADDITIONAL_REQUEST_STATUS.FILLED_INVENTORY ||
    additionalsDraftData?.requestStatusCode ===
      ADDITIONAL_REQUEST_STATUS.REQUEST_CANCELLED;

  const [notFound, setNotFound] = useState(false);
  const step = useMemo(() => {
    switch (currentStep) {
      case ADDITIONAL_VEHICLE_STEPS.GARAGE:
        return <AdditionalsVehicleGarage />;
      case ADDITIONAL_VEHICLE_STEPS.SELECT_OPTIONS:
        return <AdditionalsVehicleTabs setNotFound={setNotFound} />;
      case ADDITIONAL_VEHICLE_STEPS.REVIEW_SUBMIT:
        return <AdditionalsReviewSubmit />;
      default:
        return null;
    }
  }, [currentStep]);

  const handleAddComment = async (comment) => {
    await createAdditionalRequestNote({
      variables: {
        input: {
          additionalRequestId: requestId,
          comment,
        },
      },
    });
    setShowAddCommentModal(false);
    getAdditionalRequest({
      variables: {
        additionalRequestId: requestId,
      },
    }).then(({ data }) => {
      const activities = cloneDeep(data.additionalRequestActivities);
      const sortedActivities = activities.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      if (activities?.length) {
        dispatch(setAdditionalRequestActivities(sortedActivities));
      }
    });
  };

  const downloadAttachment = async () => {
    getDocMetadata({
      variables: {
        id: agencyGuidanceDocument?.metadataId,
      },
    }).then(async ({ data }) => {
      if (data) {
        const signedUrl = await getSignedUrl({
          variables: {
            fileKey: data?.fileLocation,
            fileType: data?.fileMimeType,
          },
        });
        if (signedUrl?.data) {
          // Download file using signedUrl.
          const response = await fetch(signedUrl?.data, {
            method: 'GET',
            headers: { 'Content-Type': agencyGuidanceDocument?.type },
          });
          // Grab blob from response
          const blob = await response.blob();
          // Browser starts download
          fileDownload(blob, agencyGuidanceDocument?.name);
        }
      }
    });
  };

  return (
    <>
      <div>
        {delinquencyCheck && !noDeliquencyCheckStatus && (
          <div className="margin-top-4">
            <Alert type="error">
              <strong>
                You are creating an order for a BOAC that has outstanding bills
                over 90 days.
              </strong>{' '}
              Until those bills are paid, the request will not be fulfilled.
            </Alert>
          </div>
        )}
        <div className="refer-replace-vehicle">
          <div className="bg-primary-lightest padding-3 margin-top-4">
            <SelectedVehicleDetails />
          </div>
          {requestId && (
            <div className="display-flex flex-column flex-align-end margin-top-5">
              <Button
                type="button"
                variant="outline"
                label="Post a comment"
                onClick={() => setShowAddCommentModal(true)}
                data-testid="post-comment"
              />
            </div>
          )}
          <CommentModal
            data-testid="add-comment-modal-referral"
            isOpen={showAddCommentModal}
            onSubmit={handleAddComment}
            onCancel={() => setShowAddCommentModal(false)}
            title="Post a comment"
            body=""
            isRequired
          />
          <div className="margin-top-8">
            <TabSet
              tabs={[
                {
                  heading: 'Selection details',
                  tabSelectedWhenOpen:
                    tab === VEHICLE_REQUEST_TAB.SELECTION_DETAILS,
                  content: (
                    <>
                      <>
                        <div className="actions-header padding-bottom-2">
                          <PageTitle
                            className="replace-vehicle-header"
                            title="Additional vehicle"
                          />
                          <Button
                            className="agency-guidance-btn"
                            type="button"
                            variant="outline"
                            onClick={() => {
                              downloadAttachment();
                            }}
                            disabled={!agencyGuidanceDocument?.contentURL}
                            label="Agency guidance"
                          />
                        </div>
                        {currentStep !== ADDITIONAL_VEHICLE_STEPS.GARAGE && (
                          <AdditionalsSelectedVehicle />
                        )}
                      </>

                      {step}
                    </>
                  ),
                },
                {
                  heading: 'Activity and comments',
                  tabSelectedWhenOpen: tab === VEHICLE_REQUEST_TAB.ACTIVITY,
                  content: <AdditionalActivities />,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
}
